import { SlateNode } from '@all-hands-hq/plex-graphql-schema';
import { Avatar, CircularProgress, IconButton, makeStyles, Snackbar, Theme, Tooltip } from '@material-ui/core';
import AddBoxIcon from '@material-ui/icons/AddBox';
import CloseIcon from '@material-ui/icons/Close';
import CommentIconMUI from '@material-ui/icons/Comment';
import DeleteIcon from '@material-ui/icons/Delete';
import classNames from 'classnames';
import React, { useState } from 'react';

import { ReactComponent as CaretDownSvg } from '../../../assets/caret_down.svg';
import { ReactComponent as CaretUpSvg } from '../../../assets/caret_up.svg';
import { ReactComponent as LikeSvg } from '../../../assets/like.svg';
import {
  ChannelType,
  CommentType,
  FullCommentFragment,
  ReactionType,
  usePromoteCommentToAnswerMutation,
} from '../../../graphql/gen/types';
import { useDeleteCommentMutation } from '../../../graphql/mutations/DeleteComment';
import { useReactToCommentMutation } from '../../../graphql/mutations/ReactToComment';
import { useGetLoggedInUserQuery } from '../../../graphql/queries/GetLoggedInUser';
import { useConfirmAction } from '../../../providers/plex_provider';
import { bottomBorderStyle, colorPalette, spacings, TextSizesEnum } from '../../../theme';
import { formatDisplayedDate, formatFullDate } from '../../../utils/commons';
import { Loader } from '../../shared/Loader';
import { ButtonColor, PlexButton } from '../../shared/primitives/PlexButton';
import { IconButtonSize, PlexIconButton } from '../../shared/primitives/PlexIconButton';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { ReadOnlyEditor } from '../../shared/slate/ReadOnlyEditor';
import { SlateEditor, SlateEditorValue } from '../../shared/slate/SlateEditor';
import { useDialogState } from '../dialog/useDialogState';
import { EmptyCommentsState } from './EmptyCommentsState';

type Props = {
  comments: FullCommentFragment[] | null | undefined;
  loading: boolean;
  replyLoading: boolean;
  onCreateComment: (input: { body: SlateNode[]; type: CommentType }) => Promise<unknown>;
  commentsVisible: boolean;
  onChangeCommentsVisibility: (visible: boolean) => void;
  showPromoteComment?: boolean;
};

type State = {
  body: SlateEditorValue;
  type: CommentType;
  error?: string | null;
};

function LikeButton({ comment }: { comment: FullCommentFragment }): React.ReactElement {
  const [reactToCommentMutation, result] = useReactToCommentMutation();
  const { currentUserReactions } = comment;
  const currentUserReacted = currentUserReactions.includes(ReactionType.Like);

  const handleClick = (): void => {
    reactToCommentMutation({
      variables: { reaction: { type: ReactionType.Like, commentId: comment.id, checked: !currentUserReacted } },
    });
  };
  const likesCount =
    comment.reactionCounts.find((reactionCount) => reactionCount.type === ReactionType.Like)?.count || 0;

  return (
    <div>
      <PlexButton
        label={`Like (${likesCount})`}
        onClick={handleClick}
        disabled={result.loading}
        SvgIconLeft={LikeSvg}
        color={currentUserReacted ? ButtonColor.TransparentBlue : ButtonColor.TransparentGray}
      />
    </div>
  );
}

function CommentToAnswerButton({ comment }: { comment: FullCommentFragment }): React.ReactElement {
  const [promoteCommentMutation, result] = usePromoteCommentToAnswerMutation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const handleClick = (): void => {
    promoteCommentMutation({
      variables: { commentId: comment.id },
    });
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarOpen(false);
  };

  return (
    <div>
      <PlexButton
        label={`Add as Answer`}
        onClick={handleClick}
        disabled={result.loading}
        MuiIconLeft={AddBoxIcon}
        color={ButtonColor.TransparentGray}
      />
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={handleCloseSnackbar}
        message="Comment added as answer"
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </div>
  );
}

function CommentToFactButton({ comment }: { comment: FullCommentFragment }): React.ReactElement {
  const [, setDialogState] = useDialogState();

  const handleClick = (): void => {
    setDialogState({ type: 'new_fact_from_comment', commentId: comment.id });
  };

  return (
    <div>
      <PlexButton
        label={`Add as Card`}
        onClick={handleClick}
        MuiIconLeft={AddBoxIcon}
        color={ButtonColor.TransparentGray}
      />
    </div>
  );
}

function TrashButton({ comment }: { comment: FullCommentFragment }): React.ReactElement {
  const [deleteCommentMutation, result] = useDeleteCommentMutation();
  const confirmAction = useConfirmAction();

  const handleDelete = (): void => {
    confirmAction({
      title: 'Confirmation',
      message: 'Are you sure you want to permanently delete this comment?',
      confirmButton: {
        onClick: (): void => {
          deleteCommentMutation({ variables: { commentId: comment.id } });
        },
        label: 'Delete',
        color: ButtonColor.RedBackground,
      },
    });
  };

  return (
    <React.Fragment>
      <IconButton onClick={handleDelete} disabled={result.loading} size="small">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );
}

const isEmpty = (body: SlateEditorValue): boolean => body.value.length === 0;

export function Comments({
  loading,
  comments,
  onCreateComment,
  replyLoading,
  commentsVisible,
  onChangeCommentsVisibility,
  showPromoteComment,
}: Props): React.ReactElement {
  const loggedInUser = useGetLoggedInUserQuery({ variables: {} });
  const [state, setState] = useState<State>({
    body: SlateEditorValue.new(),
    type: CommentType.Feedback,
  });

  const classes = useStyles();

  const handleCommentBodyChange = (body: SlateEditorValue): void => {
    setState({
      ...state,
      body,
    });
  };

  const handleClickReply = (): void => {
    const { body, type } = state;
    onCreateComment({ body: body.value, type }).then(() => {
      setState({ body: SlateEditorValue.new(), type: CommentType.Feedback });
      onChangeCommentsVisibility(true);
    });
  };

  const filteredComments = comments?.filter((comment) => !comment.deleted) || [];

  return (
    <div className={classes.root}>
      <div
        className={classNames(
          classes.writeCommentContainer,
          commentsVisible && classes.writeCommentContainerCommentsVisible
        )}
      >
        {loggedInUser.data?.currentUser && (
          <Avatar
            className={classes.formAvatarBox}
            alt={loggedInUser.data.currentUser.name}
            src={loggedInUser.data.currentUser.picture}
          />
        )}
        <SlateEditor
          value={state.body}
          initialEntities={{ expanded_link: [], mention: [] }}
          onChange={handleCommentBodyChange}
          primaryLink={null}
          onChangePrimaryLink={(): void => {
            /* do nothing */
          }}
          title="Enter your comment"
          className={classes.slateFieldInput}
          hideControls
        />
        <div className={classes.replyButton}>
          <PlexButton
            disabled={loading || replyLoading || !!state.error || isEmpty(state.body)}
            onClick={handleClickReply}
            label={
              <>
                {replyLoading && <CircularProgress className={classes.buttonProgress} size={20} />}
                Reply
              </>
            }
          />
        </div>
        <div>
          <PlexIconButton
            muiButtonClasses={{ root: classes.expandCollapseButton }}
            SvgIcon={commentsVisible ? CaretUpSvg : CaretDownSvg}
            onClick={(): void => onChangeCommentsVisibility(!commentsVisible)}
            color={ButtonColor.Gray}
            size={IconButtonSize.Normal}
            tooltip={commentsVisible ? 'Hide comments' : 'Show comments'}
          />
        </div>
      </div>
      {commentsVisible && (
        <>
          <div className={classes.commentsCount}>
            <CommentIconMUI className={classes.commentsCountIcon} />
            <PlexTypography
              text={`Comments (${comments?.length || 0})`}
              size={TextSizesEnum.Normal}
              className={classes.commentsCountText}
            />
          </div>
          {loading && (
            <div className={classes.centered}>
              <Loader />
            </div>
          )}
          {!loading && filteredComments.length === 0 && <EmptyCommentsState />}
          {!loading &&
            filteredComments.map((comment) => {
              return (
                <div className={classes.formBox} key={comment.id}>
                  <div className={classes.authorContainer}>
                    <Avatar
                      className={classes.authorAvatar}
                      alt={comment.commenter.name}
                      src={comment.commenter.picture}
                    />
                    <div className={classes.authorDataContainer}>
                      <div className={classes.authorNameContainer}>
                        <PlexTypography
                          className={classes.authorName}
                          text={comment.commenter.name}
                          size={TextSizesEnum.Normal}
                        />
                        <PlexTypography
                          className={classes.authorTitle}
                          text={comment.commenter.jobTitle || 'Employee'}
                          size={TextSizesEnum.Normal}
                        />
                      </div>
                      <div className={classes.authorNameContainer}>
                        <Tooltip title={formatFullDate(new Date(comment.createdAt))}>
                          <PlexTypography
                            className={classes.commentPostTime}
                            text={formatDisplayedDate(new Date(comment.createdAt))}
                            size={TextSizesEnum.Normal}
                          />
                        </Tooltip>
                        {comment.externalSource === ChannelType.Gmail && (
                          <PlexTypography
                            className={classes.commentChannel}
                            text={'via Email'}
                            size={TextSizesEnum.Normal}
                          />
                        )}
                        {comment.externalSource === ChannelType.Slack && (
                          <PlexTypography
                            className={classes.commentChannel}
                            text={'via Slack'}
                            size={TextSizesEnum.Normal}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={classes.commentBoxes}>
                    <div className={classes.commentContent}>
                      <ReadOnlyEditor
                        value={comment.body}
                        hideImages
                        entities={{ expanded_link: comment.links, mention: comment.mentions }}
                      />
                    </div>
                    <div className={classes.commentDetails}>
                      <div className={classes.likeContainer}>
                        <LikeButton comment={comment} />
                        {showPromoteComment && <CommentToAnswerButton comment={comment} />}
                        <CommentToFactButton comment={comment} />
                        <TrashButton comment={comment} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: colorPalette.gray._100,
    borderTopWidth: 1,
    borderTopColor: colorPalette.purpleGray._100,
    borderTopStyle: 'solid',
    overflow: 'auto',
  },
  commentsCount: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'row',
    color: colorPalette.dark._800,
    fontWeight: 500,
    padding: spacings.xlarge,
    ...bottomBorderStyle,
  },
  commentsCountIcon: {
    marginLeft: spacings.small,
    fill: `${colorPalette.purpleGray._500} !important`, // sets the svg color
  },
  commentsCountText: {
    marginLeft: spacings.normal,
    color: colorPalette.purpleGray._500,
    fontWeight: 600,
  },
  centered: {
    display: 'flex',
    flexShrink: 0,
    marginTop: theme.spacing(2),
    justifyContent: 'center',
  },
  buttonProgress: {
    marginRight: theme.spacing(1),
  },
  writeCommentContainer: {
    display: 'flex',
    flexShrink: 0,
    paddingTop: spacings.xlarge,
    paddingLeft: spacings.xlarge,
    paddingBottom: spacings.xlarge,
    width: '100%',
    flexDirection: 'row',
  },
  writeCommentContainerCommentsVisible: {
    ...bottomBorderStyle,
  },
  formBox: {
    display: 'flex',
    flexShrink: 0,
    width: '100%',
    flexDirection: 'column',
    ...bottomBorderStyle,
  },
  formAvatarBox: {
    display: 'flex',
    marginTop: spacings.small,
    flexShrink: 0,
  },
  textFieldBox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginLeft: spacings.normal,
    alignItems: 'flex-end',
  },
  slateFieldInput: {
    flexGrow: 1,
    backgroundColor: 'white',
    borderRadius: '4px',
    border: `1px solid ${colorPalette.dark['_500']}`,
    marginLeft: theme.spacing(2),
    paddingTop: theme.spacing(1),
    minHeight: theme.spacing(9),
    '&:hover': {
      borderColor: '#000',
    },
  },
  replyButton: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'column',
    marginLeft: spacings.normal,
    marginTop: spacings.small,
  },
  authorContainer: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: spacings.xlarge,
    marginTop: spacings.normal,
  },
  authorAvatar: {
    display: 'flex',
  },
  authorDataContainer: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'column',
    marginLeft: spacings.normal,
  },
  authorNameContainer: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'row',
  },
  authorName: {
    color: colorPalette.purpleGray._500,
    lineHeight: '24px',
    fontWeight: 'bold',
  },
  authorTitle: {
    color: colorPalette.purpleGray._500,
    lineHeight: '24px',
    marginLeft: spacings.xsmall,
  },
  commentPostTime: {
    color: colorPalette.purpleGray._300,
  },
  commentChannel: {
    color: colorPalette.purpleGray._500,
    marginLeft: spacings.xsmall,
  },
  commentBoxes: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginLeft: 80,
    marginTop: spacings.normal,
    marginRight: spacings.xlarge,
  },
  commentContent: {
    color: colorPalette.dark._800,
  },
  commentDetails: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    width: '100%',
    marginBottom: theme.spacing(0.5),
  },
  commenterBox: {
    marginBottom: theme.spacing(0.5),
  },
  likeContainer: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    width: '25%',
  },
  expandCollapseButton: {
    marginRight: spacings.xlarge,
    marginLeft: spacings.xlarge,
    marginTop: spacings.small,
    borderWidth: 1,
    borderColor: colorPalette.purpleGray._100,
    borderStyle: 'solid',
  },
}));
